import type { GhostNote } from "../types"

export type GhostNoteIndex = Record<number, GhostNote>

function parseTimestamp(value?: string | null) {
  if (!value) return 0
  const time = Date.parse(value)
  return Number.isNaN(time) ? 0 : time
}

export function buildGhostNoteIndex(notes: GhostNote[]): GhostNoteIndex {
  return notes.reduce<GhostNoteIndex>((accumulator, note) => {
    const existing = accumulator[note.message_id]
    if (!existing) {
      accumulator[note.message_id] = note
      return accumulator
    }

    const existingTimestamp = parseTimestamp(existing.updated_at ?? existing.created_at)
    const nextTimestamp = parseTimestamp(note.updated_at ?? note.created_at)
    if (nextTimestamp >= existingTimestamp) {
      accumulator[note.message_id] = note
    }
    return accumulator
  }, {})
}

export type GhostNoteContextCard = {
  messageId: number
  summary: string
  confidenceLabel: string
  confidenceScore?: number | null
  sources: string[]
  createdAt: string
}

export function createGhostNoteContextCards(notes: GhostNote[]): GhostNoteContextCard[] {
  const sorted = [...notes].sort((a, b) => {
    const left = parseTimestamp(a.updated_at ?? a.created_at)
    const right = parseTimestamp(b.updated_at ?? b.created_at)
    return right - left
  })

  return sorted.map((note) => {
    const sources = Array.isArray(note.confidence_sources) ? note.confidence_sources.filter(Boolean) : []
    return {
      messageId: note.message_id,
      summary: note.summary ?? "",
      confidenceLabel: (note.confidence_label ?? "unknown").toUpperCase(),
      confidenceScore: note.confidence_score ?? null,
      sources,
      createdAt: note.updated_at ?? note.created_at,
    }
  })
}

export function formatConfidenceScore(score?: number | null): string {
  if (typeof score !== "number" || Number.isNaN(score)) {
    return "—"
  }
  const clamped = Math.max(0, Math.min(1, score))
  return `${Math.round(clamped * 100)}%`
}
